import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UploadCloud } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { api } from "@/lib/api";
import { apiError, fmtBytes } from "@/lib/platform";

// mode: "new" (optionally linked to entity) | "version" (fileObject required)
export const FileUploadDialog = ({ open, onOpenChange, mode = "new", entity, fileObject, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setFile(null); setName(""); setNote(""); setProgress(0); }
  }, [open]);

  const submit = async () => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      let res;
      const cfg = { onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || 1)) * 100)), timeout: 120000 };
      if (mode === "version") {
        fd.append("note", note);
        res = await api.post(`/platform/files/${fileObject.id}/versions`, fd, cfg);
        toast.success(`Version ${res.data.current_version} uploaded`);
      } else {
        fd.append("metadata", JSON.stringify({
          name: name || undefined, note: note || undefined,
          entity_type: entity?.type, entity_id: entity?.id, module: entity?.module,
        }));
        res = await api.post("/platform/files", fd, cfg);
        toast.success("File uploaded and hashed");
      }
      onUploaded?.(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e, "Upload failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="file-upload-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "version" ? `New version of ${fileObject?.name}` : "Upload file"}</DialogTitle>
          <DialogDescription>A SHA-256 fingerprint is stored with every version for integrity checks.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-xl py-8 cursor-pointer hover:border-[#008080]/50 transition-colors">
            <UploadCloud className="w-7 h-7 text-slate-400" />
            <span className="text-sm text-slate-600">{file ? `${file.name} · ${fmtBytes(file.size)}` : "Choose a file"}</span>
            <span className="text-[11px] text-slate-400">pdf, docx, xlsx, png, jpg, txt, csv, json · max 50 MB</span>
            <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)}
                   data-testid="file-upload-input" />
          </label>
          {mode === "new" && (
            <div className="space-y-1.5">
              <Label>Display name (optional)</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="file-upload-name" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>{mode === "version" ? "What changed?" : "Note (optional)"}</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="file-upload-note" />
          </div>
          {busy && <Progress value={progress} data-testid="file-upload-progress" />}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!file || busy} className="bg-[#003366] hover:bg-[#003366]/90"
                  data-testid="file-upload-submit">
            {busy ? `Uploading ${progress}%` : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
